import { useEffect, useRef } from "react";

import { faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import mergeRefs from "merge-refs";
import { twJoin } from "tailwind-merge";

import { FIELD_BORDER, FIELD_TEXT } from "src/design-system/utils/fieldStyles";
import { HIGHLIGHT_CLASSES } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

const LAYOUT_CLASSES = "px-2.5 py-2 font-inter-normal-12px";

type BaseTextAreaProps = Omit<
  React.ComponentPropsWithoutRef<"textarea">,
  "style" | "className"
> & {
  errored?: boolean;
  hinted?: boolean;
  fullWidth?: boolean;
  isSearchHighlighted?: boolean;
  ref?: React.Ref<HTMLTextAreaElement>;
};

type AutoGrowTextAreaProps = BaseTextAreaProps & {
  autoGrow: true;
  maxGrowHeight?: number;
  resizeable?: never;
  fullHeight?: never;
};

type StandardTextAreaProps = BaseTextAreaProps & {
  autoGrow?: false;
  maxGrowHeight?: never;
  resizeable?: boolean;
  fullHeight?: boolean;
};

type TextAreaProps = AutoGrowTextAreaProps | StandardTextAreaProps;

const useAutoGrowTextarea = (enabled: boolean | undefined) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!textarea || !mirror) return;

    const syncMirror = () => {
      mirror.textContent = textarea.value + " ";
    };

    // Intercept .value setter so programmatic writes (e.g. react-hook-form
    // setValue) trigger mirror sync without needing an "input" event or
    // a React re-render.
    const proto = Object.getPrototypeOf(textarea) as HTMLTextAreaElement;
    const descriptor = Object.getOwnPropertyDescriptor(proto, "value");

    if (descriptor?.set) {
      const originalSet = descriptor.set;
      const originalGet = descriptor.get;

      Object.defineProperty(textarea, "value", {
        configurable: true,
        get(this: HTMLTextAreaElement): string {
          return originalGet?.call(this) ?? "";
        },
        set(this: HTMLTextAreaElement, newValue: string) {
          originalSet.call(this, newValue);
          syncMirror();
        },
      });
    }

    syncMirror();
    textarea.addEventListener("input", syncMirror);

    return () => {
      textarea.removeEventListener("input", syncMirror);
      Reflect.deleteProperty(textarea, "value");
    };
  }, [enabled]);

  return { textareaRef, mirrorRef };
};

export const Textarea: React.FC<TextAreaProps> = ({
  errored,
  hinted,
  resizeable = false,
  fullWidth,
  fullHeight,
  isSearchHighlighted,
  autoGrow,
  maxGrowHeight = 280,
  ref,
  ...props
}) => {
  const [outerRef] = useHighlightedScrollIntoView(isSearchHighlighted);
  const { textareaRef, mirrorRef } = useAutoGrowTextarea(autoGrow);

  return (
    <div
      ref={outerRef}
      className={twJoin(
        "relative rounded-lg border",
        !errored && !hinted && FIELD_BORDER.default,
        !errored && hinted && FIELD_BORDER.hinted,
        errored && FIELD_BORDER.errored,
        !errored &&
          "focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/25",
        autoGrow
          ? "decideScrollbar grid overflow-y-auto"
          : "grid overflow-hidden",
        fullWidth && "w-full",
        fullHeight && "h-full",
      )}
      data-loc={autoGrow ? "autogrow-grid" : undefined}
      style={autoGrow ? { maxHeight: maxGrowHeight } : undefined}
    >
      {autoGrow && (
        <div
          ref={mirrorRef}
          aria-hidden="true"
          className={twJoin(
            "pointer-events-none invisible min-h-[80px] min-w-0 whitespace-pre-wrap break-words [grid-area:1/1]",
            LAYOUT_CLASSES,
            errored && "pr-7.5",
          )}
        />
      )}
      <textarea
        ref={autoGrow ? mergeRefs(ref, textareaRef) : ref}
        className={twJoin(
          LAYOUT_CLASSES,
          "min-h-[80px] w-full focus:outline-none",
          "placeholder:text-gray-500",
          props.disabled
            ? twJoin("bg-gray-50", FIELD_TEXT.disabled)
            : twJoin(
                "cursor-text bg-white",
                hinted && !errored ? FIELD_TEXT.hinted : FIELD_TEXT.default,
              ),
          errored && "pr-7.5",
          isSearchHighlighted && HIGHLIGHT_CLASSES,
          autoGrow &&
            "min-w-0 resize-none overflow-hidden [grid-area:1/1] focus:ring-0",
          !autoGrow && "decideScrollbar h-20",
          !autoGrow && !resizeable && "resize-none",
          !autoGrow && fullHeight && "h-full",
        )}
        {...props}
      />
      {errored && (
        <FontAwesomeIcon
          className="absolute top-3.5 right-4 font-inter-normal-12px text-red-600"
          icon={faWarning}
        />
      )}
    </div>
  );
};
